import { ErrorOutline, InfoOutlined } from '@mui/icons-material';
import {
	Box,
	Checkbox,
	Divider,
	FormControlLabel,
	Tooltip,
	Typography
} from '@mui/material';
import React, { memo, useState } from 'react';

import { canEditSitemapCollection, labelSx } from 'constants/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import SitemapDatePicker from 'components/SitemapDatePicker';

interface SitemapOptionsProps {
	showInHtmlSitemap: boolean;
	showInXmlSitemap: boolean;
	addToSitemapOn: string;
	removeFromSitemapOn: string;
	startDateError: string | null;
	endDateError: string | null;
	noIndex: boolean;
	noFollow: boolean;
	isSiteOwnedPage: boolean;
	onCheckboxChange: (field: string, value: boolean) => void;
	onStartDateChange: (value: string) => void;
	onEndDateChange: (value: string) => void;
	onStartDateErrorChange: (error: string | null) => void;
}

const SitemapOptions = ({
	showInHtmlSitemap,
	showInXmlSitemap,
	addToSitemapOn,
	removeFromSitemapOn,
	startDateError,
	endDateError,
	noIndex,
	noFollow,
	isSiteOwnedPage,
	onCheckboxChange,
	onStartDateChange,
	onEndDateChange,
	onStartDateErrorChange
}: SitemapOptionsProps) => {
	const intl = useIntl();
	const [startDateOpen, setStartDateOpen] = useState(false);
	const [endDateOpen, setEndDateOpen] = useState(false);

	return (
		<>
			{/* Sitemap Checkboxes + date fields – only shown with CustomSitemap access */}
			{canEditSitemapCollection && (
				<>
					<Box sx={{ mt: 1.5 }}>
						<FormControlLabel
							sx={{ width: 'fit-content', mb: -0.5 }}
							control={
								<Checkbox
									checked={showInHtmlSitemap}
									onChange={e =>
										onCheckboxChange('showInHtmlSitemap', e.target.checked)
									}
									sx={{ py: '6px' }}
								/>
							}
							label={
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
										fontSize: 14
									}}
								>
									<FormattedMessage id="SHOW_IN_HTML_SITEMAP" />
									<Tooltip
										title={
											<FormattedMessage id="SHOW_IN_HTML_SITEMAP_TOOLTIP" />
										}
									>
										<InfoOutlined
											sx={{
												fontSize: 12,
												color: 'text.secondary',
												verticalAlign: 'super',
												position: 'relative',
												top: -4
											}}
										/>
									</Tooltip>
								</Box>
							}
						/>
						<FormControlLabel
							sx={{ width: 'fit-content' }}
							control={
								<Checkbox
									checked={showInXmlSitemap}
									onChange={e =>
										onCheckboxChange('showInXmlSitemap', e.target.checked)
									}
									sx={{ py: '6px' }}
								/>
							}
							label={
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
										fontSize: 14
									}}
								>
									<FormattedMessage id="SHOW_IN_XML_SITEMAP" />
									<Tooltip
										title={
											<FormattedMessage id="SHOW_IN_XML_SITEMAP_TOOLTIP" />
										}
									>
										<InfoOutlined
											sx={{
												fontSize: 12,
												color: 'text.secondary',
												verticalAlign: 'super',
												position: 'relative',
												top: -4
											}}
										/>
									</Tooltip>
								</Box>
							}
						/>
					</Box>

					{/* Date fields – only shown when Show in XML Sitemap is checked */}
					{showInXmlSitemap && (
						<>
							<Box sx={{ mt: 1.5, ml: '30px' }}>
								<Typography variant="body2" sx={{ ...labelSx, mb: 0.5 }}>
									<FormattedMessage id="SITEMAP_START_DATE" />:
								</Typography>
								<SitemapDatePicker
									open={startDateOpen}
									onOpen={() => {
										setEndDateOpen(false);
										setStartDateOpen(true);
									}}
									onClose={() => setStartDateOpen(false)}
									value={addToSitemapOn || null}
									onChange={newValue => {
										onStartDateChange(newValue);
										if (newValue) {
											onStartDateErrorChange(null);
										} else {
											onStartDateErrorChange(
												intl.formatMessage({
													id: 'SITEMAP_START_DATE_REQUIRED',
													defaultMessage: 'Start date is required.'
												})
											);
										}
									}}
								/>
								{startDateError && (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 0.5,
											mt: 0.5
										}}
									>
										<ErrorOutline sx={{ fontSize: 14, color: 'error.main' }} />
										<Typography
											variant="caption"
											color="error.main"
											sx={{
												letterSpacing: 'normal',
												lineHeight: 'normal'
											}}
										>
											{startDateError}
										</Typography>
									</Box>
								)}
							</Box>

							<Box sx={{ mt: 2, ml: '30px' }}>
								<Typography variant="body2" sx={{ ...labelSx, mb: 0.5 }}>
									<FormattedMessage id="SITEMAP_END_DATE" />:
								</Typography>
								<SitemapDatePicker
									open={endDateOpen}
									onOpen={() => {
										setStartDateOpen(false);
										setEndDateOpen(true);
									}}
									onClose={() => setEndDateOpen(false)}
									value={removeFromSitemapOn || null}
									onChange={onEndDateChange}
								/>
								{endDateError && (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 0.5,
											mt: 0.5
										}}
									>
										<ErrorOutline sx={{ fontSize: 14, color: 'error.main' }} />
										<Typography
											variant="caption"
											color="error.main"
											sx={{
												letterSpacing: 'normal',
												lineHeight: 'normal'
											}}
										>
											{endDateError}
										</Typography>
									</Box>
								)}
							</Box>
						</>
					)}
				</>
			)}

			<Box sx={{ mt: 2, mb: 0.5 }}>
				<Divider>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ textTransform: 'uppercase' }}
					>
						<FormattedMessage id="OR" />
					</Typography>
				</Divider>
			</Box>

			{/* SEO Checkboxes – grouped tight */}
			<Box sx={{ display: 'flex', flexDirection: 'column' }}>
				<FormControlLabel
					sx={{ width: 'fit-content', mb: -0.5 }}
					control={
						<Checkbox
							checked={noIndex}
							onChange={e => onCheckboxChange('noIndex', e.target.checked)}
							disabled={!isSiteOwnedPage}
							sx={{ py: '6px' }}
						/>
					}
					label={
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								fontSize: 14
							}}
						>
							<FormattedMessage id="NO_INDEX" />
							<Tooltip title={<FormattedMessage id="NO_INDEX_TOOLTIP" />}>
								<InfoOutlined
									sx={{
										fontSize: 12,
										color: 'text.secondary',
										verticalAlign: 'super',
										position: 'relative',
										top: -4
									}}
								/>
							</Tooltip>
						</Box>
					}
				/>
				<FormControlLabel
					sx={{ width: 'fit-content' }}
					control={
						<Checkbox
							checked={noFollow}
							onChange={e => onCheckboxChange('noFollow', e.target.checked)}
							disabled={!isSiteOwnedPage}
							sx={{ py: '6px' }}
						/>
					}
					label={
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								fontSize: 14
							}}
						>
							<FormattedMessage id="NO_FOLLOW" />
							<Tooltip title={<FormattedMessage id="NO_FOLLOW_TOOLTIP" />}>
								<InfoOutlined
									sx={{
										fontSize: 12,
										color: 'text.secondary',
										verticalAlign: 'super',
										position: 'relative',
										top: -4
									}}
								/>
							</Tooltip>
						</Box>
					}
				/>
			</Box>
		</>
	);
};

export default memo(SitemapOptions);
